import React, { useState } from 'react';
import { Download, Mail, FileText, Loader2 } from 'lucide-react';
import WhatsAppIcon from '../icons/WhatsAppIcon';
import { toast } from 'react-toastify';
import { generateReceipt, emailReceipt } from '../../api/receipts';
import EmailReceiptModal from './EmailReceiptModal';
import WhatsAppSendModal from './WhatsAppSendModal';

/**
 * ReceiptActions Component
 * Provides actions for receipt generation, download, and email
 */
const ReceiptActions = ({ sale, onPreview }) => {
  const [loading, setLoading] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
  const [printLoading, setPrintLoading] = useState(false);

  /**
   * Handle receipt PDF download
   */
  const handleDownload = async () => {
    setLoading(true);
    try {
      const blob = await generateReceipt(sale.id, true);

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `receipt-${sale.receipt_number}.pdf`;
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Receipt downloaded successfully');
    } catch (error) {
      toast.error('Failed to download receipt');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle receipt preview (in modal if callback provided, otherwise new tab)
   */
  const handlePreview = async () => {
    if (onPreview) {
      // Use the provided preview handler (for modal)
      onPreview();
    } else {
      // Fallback to new tab
      setLoading(true);
      try {
        const blob = await generateReceipt(sale.id, false);
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank');
        setTimeout(() => {
          window.URL.revokeObjectURL(url);
        }, 1000);
        toast.success('Receipt opened in new tab');
      } catch (error) {
        toast.error('Failed to preview receipt');
      } finally {
        setLoading(false);
      }
    }
  };

  /**
   * Handle print receipt - fixed flashing issue
   */
  const handlePrint = async () => {
    setPrintLoading(true);
    try {
      const blob = await generateReceipt(sale.id, false);
      const url = window.URL.createObjectURL(blob);

      // Create iframe for printing
      const iframe = document.createElement('iframe');
      iframe.style.position = 'absolute';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = 'none';
      iframe.src = url;
      document.body.appendChild(iframe);

      // Wait for load and print
      iframe.onload = function () {
        setTimeout(() => {
          try {
            iframe.contentWindow.focus();
            iframe.contentWindow.print();
          } catch (e) {
            // Fallback: open in new window if iframe print fails
            window.open(url, '_blank');
          }
        }, 250);

        // Cleanup after a longer delay to ensure print dialog is shown
        setTimeout(() => {
          if (document.body.contains(iframe)) {
            document.body.removeChild(iframe);
          }
          window.URL.revokeObjectURL(url);
          setPrintLoading(false);
        }, 5000);
      };

      // Handle errors
      iframe.onerror = function () {
        toast.error('Failed to load receipt for printing');
        if (document.body.contains(iframe)) {
          document.body.removeChild(iframe);
        }
        window.URL.revokeObjectURL(url);
        setPrintLoading(false);
      };

    } catch (error) {
      toast.error('Failed to print receipt');
      setPrintLoading(false);
    }
  };

  return (
    <>
      <div className="flex flex-wrap gap-2">
        {/* Download Button */}
        <button
          type="button"
          onClick={handleDownload}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-accent-gold text-white rounded-lg hover:bg-accent-gold/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
        >
          {loading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Download className="w-4 h-4" />
          )}
          <span>Download PDF</span>
        </button>

        {/* Preview Button */}
        <button
          type="button"
          onClick={handlePreview}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-secondary-lightGray text-secondary-gray rounded-lg hover:bg-secondary-offWhite transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
        >
          <FileText className="w-4 h-4" />
          <span>Preview</span>
        </button>

        {/* Print Button */}
        <button
          type="button"
          onClick={handlePrint}
          disabled={loading || printLoading}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-secondary-lightGray text-secondary-gray rounded-lg hover:bg-secondary-offWhite transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
        >
          {printLoading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Download className="w-4 h-4" />
          )}
          <span>{printLoading ? 'Preparing...' : 'Print'}</span>
        </button>

        {/* Email Button */}
        <button
          type="button"
          onClick={() => setShowEmailModal(true)}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-secondary-lightGray text-secondary-gray rounded-lg hover:bg-secondary-offWhite transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
        >
          <Mail className="w-4 h-4" />
          <span>Email</span>
        </button>

        {/* WhatsApp Button */}
        <button
          type="button"
          onClick={() => setShowWhatsAppModal(true)}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-secondary-lightGray text-secondary-gray rounded-lg hover:bg-secondary-offWhite transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
        >
          <WhatsAppIcon className="w-4 h-4" />
          <span>WhatsApp</span>
        </button>
      </div>

      {/* Email Modal */}
      {showEmailModal && (
        <EmailReceiptModal
          sale={sale}
          onClose={() => setShowEmailModal(false)}
        />
      )}

      {/* WhatsApp Modal */}
      {showWhatsAppModal && (
        <WhatsAppSendModal
          sale={sale}
          onClose={() => setShowWhatsAppModal(false)}
          onSuccess={() => {
            toast.success('Receipt sent via WhatsApp');
          }}
        />
      )}
    </>
  );
};

export default ReceiptActions;
